import type { CstElement, CstNode, IToken } from 'chevrotain';
import { isCstNode } from 'millan';

import { numericSort } from 'bones/utils/sort/numericSort';

const findFirstToken = (cstElement: CstElement): IToken | undefined => {
  if (isCstNode(cstElement)) {
    let found: IToken | undefined;
    Object.values(cstElement.children).forEach((children) => {
      if (found) {
        return;
      }

      children.forEach((child) => {
        const token = findFirstToken(child);
        if (token) {
          found = token;
        }
      });
    });
    return found;
  }
  return cstElement;
};

export const groupMany = <Parser>(
  parent: CstNode,
  ruleGroups: Array<Array<keyof Parser>>
): CstNode[] => {
  const ruleNames = new Set(ruleGroups.flat());

  let currentGroup: Array<keyof Parser> | undefined;
  let currentRuleIdx = 0;
  let currentResult: Array<[string, CstElement]> = [];
  const results: Array<Array<[string, CstElement]>> = [];
  const sorted = Object.entries(parent.children)
    .reduce<Array<[string, CstElement]>>((acc, [ruleName, children]) => {
      if (ruleNames.has(ruleName as any)) {
        acc.push(
          ...children.map((child) => [ruleName, child] as [string, CstElement])
        );
      }
      return acc;
    }, [])
    .sort(
      numericSort(([, cstElement]) => {
        const token = findFirstToken(cstElement);
        return token ? token.startOffset : /* istanbul ignore next */ 0;
      })
    );

  sorted.forEach(([ruleName, cstElement]) => {
    // add this rule to the existing result group
    if (currentGroup && currentGroup[currentRuleIdx] === ruleName) {
      currentResult.push([ruleName, cstElement]);
      currentRuleIdx += 1;
      return;
    }

    // start a new result group
    if (currentResult.length) {
      results.push(currentResult);
    }
    currentResult = [];

    // find a new result group
    currentGroup = ruleGroups.find((group) => group[0] === ruleName);
    currentRuleIdx = 0;

    // add this rule to the new result group
    if (currentGroup) {
      currentResult.push([ruleName, cstElement]);
      currentRuleIdx += 1;
    }
  });

  if (currentResult.length) {
    results.push(currentResult);
  }

  return results.map((children) => ({
    ...parent,
    children: children.reduce<CstNode['children']>(
      (acc, [ruleName, cstElement]) => {
        acc[ruleName] = [cstElement];
        return acc;
      },
      {}
    ),
  }));
};
